import { closestAttributeValue } from '../../common/closest-attribute-value.mjs'
import {
  validateConfig,
  ConfigurableComponent,
  configOverride
} from '../../common/configuration.mjs'
import { createElement } from '../../common/create-element.mjs'
import { formatErrorMessage } from '../../common/index.mjs'
import { ConfigError, ElementError, SupportError } from '../../errors/index.mjs'
import { I18n } from '../../i18n.mjs'

/**
 * Character count component
 *
 * Tracks the number of characters or words in the `.govuk-js-character-count`
 * `<textarea>` inside the element. Displays a message with the remaining number
 * of characters/words available, or the number of characters/words in excess.
 *
 * You can configure the message to only appear after a certain percentage
 * of the available characters/words has been entered.
 *
 * @preserve
 * @augments ConfigurableComponent<CharacterCountConfig>
 */
export class CharacterCount extends ConfigurableComponent {
  /** @private */
  $textarea

  /** @private */
  count = 0

  /**
   * @type {Intl.Segmenter | null}
   */
  segmenter = null

  /** @private */
  $visibleCountMessage

  /** @private */
  $screenReaderCountMessage

  /**
   * @private
   * @type {number | null}
   */
  lastInputTimestamp = null

  /** @private */
  lastInputValue = ''

  /**
   * @private
   * @type {number | null}
   */
  valueChecker = null

  /** @private */
  i18n

  /** @private */
  maxLength;

  /**
   * Character count config override
   *
   * @internal
   * @param {Partial<CharacterCountConfig>} datasetConfig - configuration specified by dataset
   * @returns {Partial<CharacterCountConfig>} - configuration to override by dataset
   */
  [configOverride](datasetConfig) {
    let configOverrides = /** @type {Partial<CharacterCountConfig>} */ ({})

    // To ensure data-attributes take complete precedence, even if they change
    // the type of count, we need to reset the `maxlength` and `maxwords` from
    // the JavaScript config
    if ('maxwords' in datasetConfig || 'maxlength' in datasetConfig) {
      configOverrides = {
        maxlength: undefined,
        maxwords: undefined
      }
    }

    if ('maxwords' in datasetConfig || this.config.maxwords !== undefined) {
      console.warn(
        formatErrorMessage(
          CharacterCount,
          'Option `maxwords` is deprecated. Use `maxlength` with `countType: "words"` instead.'
        )
      )

      if (!('maxlength' in datasetConfig)) {
        configOverrides.maxlength =
          datasetConfig.maxwords ?? this.config.maxwords
        configOverrides.countType = 'words'
      }
    }

    return configOverrides
  }

  /**
   * @param {Element | null} $root - HTML element to use for character count
   * @param {CharacterCountConfig} [config] - Character count config
   */
  constructor($root, config = {}) {
    super($root, config)

    const $textarea = this.$root.querySelector('.govuk-js-character-count')
    if (
      !(
        $textarea instanceof HTMLTextAreaElement ||
        $textarea instanceof HTMLInputElement
      )
    ) {
      throw new ElementError({
        component: CharacterCount,
        element: $textarea,
        expectedType: 'HTMLTextareaElement or HTMLInputElement',
        identifier: 'Form field (`.govuk-js-character-count`)'
      })
    }

    // Check for valid config
    const errors = validateConfig(CharacterCount.schema, this.config)
    if (errors[0]) {
      throw new ConfigError(formatErrorMessage(CharacterCount, errors[0]))
    }

    this.i18n = new I18n(this.config.i18n, {
      // Read the fallback if necessary rather than have it set in the defaults
      locale: closestAttributeValue(this.$root, 'lang')
    })

    if (
      this.config.countType === 'characters' ||
      (this.config.countType === 'words' && this.config.maxwords === undefined)
    ) {
      if (!('Segmenter' in Intl)) {
        throw new SupportError(
          formatErrorMessage(
            CharacterCount,
            'Support for "Intl.Segmenter" required'
          )
        )
      }

      this.segmenter = new Intl.Segmenter(this.i18n.locale, {
        granularity: this.config.countType === 'words' ? 'word' : 'grapheme'
      })
    }

    // Determine the limit attribute (characters or words)
    this.maxLength = this.config.maxlength ?? Infinity

    this.$textarea = $textarea

    const textareaDescriptionId = `${this.$textarea.id}-info`
    const $textareaDescription = document.getElementById(textareaDescriptionId)
    if (!$textareaDescription) {
      throw new ElementError({
        component: CharacterCount,
        element: $textareaDescription,
        identifier: `Count message (\`id="${textareaDescriptionId}"\`)`
      })
    }

    // Pre-existing validation error rendered from server
    this.$errorMessage = this.$root.querySelector('.govuk-error-message')

    // Inject a description for the textarea if none is present already
    // for when the component was rendered with no maxlength nor custom
    // textareaDescriptionText
    // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
    if ($textareaDescription.textContent.match(/^\s*$/)) {
      $textareaDescription.textContent = this.i18n.t('textareaDescription', {
        count: this.maxLength
      })
    }

    // Move the textarea description to be immediately after the textarea
    // Kept for backwards compatibility
    this.$textarea.insertAdjacentElement('afterend', $textareaDescription)

    // Create the *screen reader* specific live-updating counter
    // This doesn't need any styling classes, as it is never visible
    const $screenReaderCountMessage = createElement('div', {
      class: 'govuk-character-count__sr-status govuk-visually-hidden',
      'aria-live': 'polite'
    })

    this.$screenReaderCountMessage = $screenReaderCountMessage
    $textareaDescription.insertAdjacentElement(
      'afterend',
      $screenReaderCountMessage
    )

    // Create our live-updating counter element, copying the classes from the
    // textarea description for backwards compatibility as these may have been
    // configured
    const $visibleCountMessage = createElement('div', {
      class: `${$textareaDescription.className} govuk-character-count__status`,
      'aria-hidden': 'true'
    })

    this.$visibleCountMessage = $visibleCountMessage
    $textareaDescription.insertAdjacentElement('afterend', $visibleCountMessage)

    // Hide the textarea description
    $textareaDescription.classList.add('govuk-visually-hidden')

    // Remove hard limit if set
    this.$textarea.removeAttribute('maxlength')

    this.bindChangeEvents()

    // When the page is restored after navigating 'back' in some browsers the
    // state of form controls is not restored until *after* the DOMContentLoaded
    // event is fired, so we need to sync after the pageshow event.
    window.addEventListener('pageshow', () => {
      // If the current value of the textarea is the same as what's
      // in the HTML, don't re-run when users have not edited the field yet
      // (new page load or BF cache navigation without having edited).
      if (this.$textarea.value !== this.$textarea.textContent) {
        this.updateCount()
        this.updateCountMessage()
      }
    })

    // Although we've set up handlers to sync state on the pageshow event, init
    // could be called after those events have fired, for example if they are
    // added to the page dynamically, so update now too.
    this.updateCount()
    this.updateCountMessage()
  }

  /**
   * Bind change events
   *
   * Set up event listeners on the $textarea so that the count messages update
   * when the user types.
   *
   * @private
   */
  bindChangeEvents() {
    this.$textarea.addEventListener('input', () => this.handleInput())

    // Bind focus/blur events to start/stop polling
    this.$textarea.addEventListener('focus', () => this.handleFocus())
    this.$textarea.addEventListener('blur', () => this.handleBlur())
  }

  /**
   * Handle input event
   *
   * Update the visible character counter and keep track of when the last update
   * happened for each keypress
   *
   * @private
   */
  handleInput() {
    this.updateCount()
    this.updateVisibleCountMessage()
    this.lastInputTimestamp = Date.now()
  }

  /**
   * Handle focus event
   *
   * Speech recognition software such as Dragon NaturallySpeaking will modify
   * the fields by directly changing its `value`. These changes don't trigger
   * events in JavaScript, so we need to poll to handle when and if they occur.
   *
   * Once the keyup event hasn't been detected for at least 1000 ms (1s), check
   * if the textarea value has changed and update the count message if it has.
   *
   * This is so that the update triggered by the manual comparison doesn't
   * conflict with debounced KeyboardEvent updates.
   *
   * @private
   */
  handleFocus() {
    this.valueChecker = window.setInterval(() => {
      if (
        !this.lastInputTimestamp ||
        Date.now() - 500 >= this.lastInputTimestamp
      ) {
        this.updateIfValueChanged()
      }
    }, 1000)
  }

  /**
   * Handle blur event
   *
   * Stop checking the textarea value once the textarea no longer has focus
   *
   * @private
   */
  handleBlur() {
    // Cancel value checking on blur
    if (this.valueChecker) {
      window.clearInterval(this.valueChecker)
    }
  }

  /**
   * Update count message if textarea value has changed
   *
   * @private
   */
  updateIfValueChanged() {
    if (this.$textarea.value !== this.lastInputValue) {
      this.lastInputValue = this.$textarea.value
      this.updateCountMessage()
    }
  }

  /**
   * Update count message
   *
   * Helper function to update both the visible and screen reader-specific
   * counters simultaneously (e.g. on init)
   *
   * @private
   */
  updateCountMessage() {
    this.updateVisibleCountMessage()
    this.updateScreenReaderCountMessage()
  }

  /**
   * Update visible count message
   *
   * @private
   */
  updateVisibleCountMessage() {
    const remainingNumber = this.maxLength - this.count
    const isError = remainingNumber < 0

    // If input is over the threshold, remove the disabled class which renders
    // the counter invisible.
    this.$visibleCountMessage.classList.toggle(
      'govuk-character-count__message--disabled',
      !this.isOverThreshold()
    )

    // Update styles
    if (!this.$errorMessage) {
      // Only toggle the textarea error class if there isn't an error message
      // already, as it may be unrelated to the limit (eg: allowed characters)
      // and would set the border colour back to black.
      this.$textarea.classList.toggle('govuk-textarea--error', isError)
    }
    this.$visibleCountMessage.classList.toggle('govuk-error-message', isError)
    this.$visibleCountMessage.classList.toggle('govuk-hint', !isError)

    // Update message
    this.$visibleCountMessage.textContent = this.getCountMessage()
  }

  /**
   * Update screen reader count message
   *
   * @private
   */
  updateScreenReaderCountMessage() {
    // If over the threshold, remove the aria-hidden attribute, allowing screen
    // readers to announce the content of the element.
    if (this.isOverThreshold()) {
      this.$screenReaderCountMessage.removeAttribute('aria-hidden')
    } else {
      this.$screenReaderCountMessage.setAttribute('aria-hidden', 'true')
    }

    // Update message
    this.$screenReaderCountMessage.textContent = this.getCountMessage()
  }

  /**
   * Count the number of characters (or words) in the given text, using the
   * configured count type, and update the component-wide count
   *
   * @private
   */
  updateCount() {
    const text = this.$textarea.value

    // Count grapheme clusters (user-perceived characters)
    if (this.config.countType === 'characters') {
      this.count = this.segmenter
        ? Array.from(this.segmenter.segment(text)).length
        : 0

      return
    }

    // Count words
    //
    // If the (deprecated) `maxwords` option is set, count consecutive
    // non-whitespace results rather than using the segmenter
    if (this.config.countType === 'words') {
      if (this.config.maxwords !== undefined) {
        this.count = text.match(/\S+/g)?.length ?? 0
        return
      }

      const segments = this.segmenter
        ? Array.from(this.segmenter.segment(text))
        : []

      // Filter out punctuation and whitespace, leaving only words
      this.count = segments.filter((segment) => segment.isWordLike).length
      return
    }

    this.count = text.length
  }

  /**
   * Get count message
   *
   * @private
   * @returns {string} Status message
   */
  getCountMessage() {
    const remainingNumber = this.maxLength - this.count
    return this.formatCountMessage(remainingNumber)
  }

  /**
   * Formats the message shown to users according to what's counted
   * and how many remain
   *
   * @private
   * @param {number} remainingNumber - The number of words/characters remaining
   * @param {CharacterCountConfig['countType']} [countType] - Deprecated
   * @returns {string} Status message
   */
  formatCountMessage(remainingNumber, countType) {
    countType = countType ?? this.config.countType

    let translationKeyPrefix = 'characters'
    let translationKeySuffix = remainingNumber < 0 ? 'OverLimit' : 'UnderLimit'

    if (countType === 'words') {
      translationKeyPrefix = 'words'
    }

    if (remainingNumber === 0) {
      translationKeySuffix = 'AtLimit'
    }

    return this.i18n.t(`${translationKeyPrefix}${translationKeySuffix}`, {
      count: remainingNumber === 0 ? undefined : Math.abs(remainingNumber)
    })
  }

  /**
   * Check if count is over threshold
   *
   * Checks whether the value is over the configured threshold for the input.
   * If there is no configured threshold, it is set to 0 and this function will
   * always return true.
   *
   * @private
   * @returns {boolean} true if the current count is over the config.threshold
   *   (or no threshold is set)
   */
  isOverThreshold() {
    // No threshold means we're always above threshold so save some computation
    if (!this.config.threshold) {
      return true
    }

    // Determine the remaining number of characters/words
    const currentLength = this.count
    const maxLength = this.maxLength

    const thresholdValue = (maxLength * this.config.threshold) / 100

    return thresholdValue <= currentLength
  }

  /**
   * Name for the component used when initialising using data-module attributes.
   */
  static moduleName = 'govuk-character-count'

  /**
   * Character count default config
   *
   * @see {@link CharacterCountConfig}
   * @constant
   * @type {CharacterCountConfig}
   */
  static defaults = Object.freeze({
    threshold: 0,
    countType: 'length',
    i18n: {
      // Characters
      charactersUnderLimit: {
        one: 'You have %{count} character remaining',
        other: 'You have %{count} characters remaining'
      },
      charactersAtLimit: 'You have 0 characters remaining',
      charactersOverLimit: {
        one: 'You have %{count} character too many',
        other: 'You have %{count} characters too many'
      },
      // Words
      wordsUnderLimit: {
        one: 'You have %{count} word remaining',
        other: 'You have %{count} words remaining'
      },
      wordsAtLimit: 'You have 0 words remaining',
      wordsOverLimit: {
        one: 'You have %{count} word too many',
        other: 'You have %{count} words too many'
      },
      textareaDescription: {
        other: ''
      }
    }
  })

  /**
   * Character count config schema
   *
   * @constant
   * @satisfies {Schema<CharacterCountConfig>}
   */
  static schema = Object.freeze({
    properties: {
      i18n: { type: 'object' },
      maxwords: { type: 'number' },
      maxlength: { type: 'number' },
      threshold: { type: 'number' },
      countType: { type: 'string' }
    },
    anyOf: [
      {
        required: ['maxwords'],
        errorMessage: 'Either "maxlength" or "maxwords" must be provided'
      },
      {
        required: ['maxlength'],
        errorMessage: 'Either "maxlength" or "maxwords" must be provided'
      }
    ]
  })
}

/**
 * Character count config
 *
 * @see {@link CharacterCount.defaults}
 * @typedef {object} CharacterCountConfig
 * @property {number} [maxlength] - The maximum number of characters (or words
 *   if `countType` is set to `"words"`).
 * @property {number} [maxwords] - Deprecated. Use `maxlength` and
 *   `countType: "words"` instead.
 * @property {number} [threshold=0] - The percentage value of the limit at
 *   which point the count message is displayed. If this attribute is set, the
 *   count message will be hidden by default.
 * @property {'characters' | 'length' | 'words'} [countType] - The count type
 *   (`"characters"`, `"length"` or `"words"`) used to count the text.
 * @property {CharacterCountTranslations} [i18n=CharacterCount.defaults.i18n] - Character count translations
 */

/**
 * Character count translations
 *
 * @see {@link CharacterCount.defaults.i18n}
 * @typedef {object} CharacterCountTranslations
 *
 * Messages shown to users as they type. It provides feedback on how many words
 * or characters they have remaining or if they are over the limit. This also
 * includes a message used as an accessible description for the textarea.
 * @property {TranslationPluralForms} [charactersUnderLimit] - Message displayed
 *   when the number of characters is under the configured maximum, `maxlength`.
 *   This message is displayed visually and through assistive technologies. The
 *   component will replace the `%{count}` placeholder with the number of
 *   remaining characters. This is a [pluralised list of
 *   messages](https://frontend.design-system.service.gov.uk/localise-govuk-frontend).
 * @property {string} [charactersAtLimit] - Message displayed when the number of
 *   characters reaches the configured maximum, `maxlength`. This message is
 *   displayed visually and through assistive technologies.
 * @property {TranslationPluralForms} [charactersOverLimit] - Message displayed
 *   when the number of characters is over the configured maximum, `maxlength`.
 *   This message is displayed visually and through assistive technologies. The
 *   component will replace the `%{count}` placeholder with the number of
 *   remaining characters. This is a [pluralised list of
 *   messages](https://frontend.design-system.service.gov.uk/localise-govuk-frontend).
 * @property {TranslationPluralForms} [wordsUnderLimit] - Message displayed when
 *   the number of words is under the configured maximum, `maxlength`. This
 *   message is displayed visually and through assistive technologies. The
 *   component will replace the `%{count}` placeholder with the number of
 *   remaining words. This is a [pluralised list of
 *   messages](https://frontend.design-system.service.gov.uk/localise-govuk-frontend).
 * @property {string} [wordsAtLimit] - Message displayed when the number of
 *   words reaches the configured maximum, `maxlength`. This message is
 *   displayed visually and through assistive technologies.
 * @property {TranslationPluralForms} [wordsOverLimit] - Message displayed when
 *   the number of words is over the configured maximum, `maxlength`. This
 *   message is displayed visually and through assistive technologies. The
 *   component will replace the `%{count}` placeholder with the number of
 *   remaining words. This is a [pluralised list of
 *   messages](https://frontend.design-system.service.gov.uk/localise-govuk-frontend).
 * @property {TranslationPluralForms} [textareaDescription] - Message made
 *   available to assistive technologies, if none is already present in the
 *   HTML, to describe that the component accepts only a limited amount of
 *   content. It is visible on the page if `countType` is not supported or
 *   JavaScript is unavailable. The component will replace the `%{count}`
 *   placeholder with the value of the `maxlength` parameter.
 */

/**
 * @import { Schema } from '../../common/configuration.mjs'
 * @import { TranslationPluralForms } from '../../i18n.mjs'
 */
